<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useTranslationUtils } from '#layers/i18n/public';
import { useWorkspaceStore } from '#layers/workspace/stores/workspace';

export interface SpacebarItemProps {
	routeName?: string;
	routeParams?: Record<string, string>;
	label: string;
	icon?: string | null;
	active?: boolean;
	class?: string;
	prefetch?: boolean;
}

// `prefetch` defaults to undefined (not the Vue boolean-prop cast to false), so only items that
// explicitly pass `:prefetch="false"` opt out; every other item keeps NuxtLink's default prefetch.
const props = withDefaults(defineProps<SpacebarItemProps>(), {
	prefetch: undefined,
});

defineEmits<{
	click: [];
}>();

const { translateString } = useTranslationUtils();
const { currentWorkspaceSlug } = storeToRefs(useWorkspaceStore());

const resolvedLabel = computed(() => translateString(props.label));
const resolvedIcon = computed(() => props.icon || 'lucide:file');

const resolvedRoute = computed(() => {
	if (!props.routeName) return;

	if (!currentWorkspaceSlug.value) {
		return { name: props.routeName };
	}

	return {
		name: props.routeName,
		params: {
			workspace: currentWorkspaceSlug.value,
			...props.routeParams,
		},
	};
});
</script>

<template>
	<DTooltip :text="resolvedLabel" :content="{ side: 'right' }">
		<DButton
			class="flex size-9 items-center justify-center gap-2 rounded-md p-1.5 hover:bg-elevated focus-visible:bg-elevated"
			inactive-class="hover:bg-muted"
			:class="{
				[props.class ?? '']: !!props.class,
			}"
			:active
			active-class="bg-primary/10 text-primary! hover:bg-primary/15! focus-visible:bg-primary/15!"
			variant="ghost"
			color="neutral"
			:to="resolvedRoute"
			:prefetch
			:icon="resolvedIcon"
			:aria-label="resolvedLabel"
			size="lg"
			@click="$emit('click')"
		>
			<slot />
		</DButton>
	</DTooltip>
</template>
